import { forwardRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { Bar, BarChart, Line, LineChart, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Badge } from '@/components/ui/badge';
import type { AskResult } from '@/lib/admin/insights-api';

type Props = {
  question: string;
  result: AskResult;
};

const CONFIDENCE_LABEL: Record<string, string> = {
  high: 'High confidence',
  moderate: 'Moderate confidence',
  low: 'Low confidence, indicative only',
};

function Section({ label, body }: { label: string; body?: string | null }) {
  if (!body || !body.trim()) return null;
  return (
    <div className="border-t border-border pt-4 first:border-t-0 first:pt-0">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-primary mb-1">
        {label}
      </div>
      <div className="prose prose-sm max-w-none dark:prose-invert">
        <ReactMarkdown>{body}</ReactMarkdown>
      </div>
    </div>
  );
}

export const AskResultView = forwardRef<HTMLDivElement, Props>(function AskResultView(
  { question, result },
  ref,
) {
  const safeSeries = (result.chart?.series ?? []).map((p: { label: unknown; value: unknown }) => ({
    label:
      typeof p.label === 'string' || typeof p.label === 'number'
        ? String(p.label)
        : JSON.stringify(p.label),
    value: typeof p.value === 'number' ? p.value : Number(p.value) || 0,
  }));

  // Older answers only carried the two-part shape.
  const situation = result.situation_markdown ?? result.answer_markdown ?? '';

  return (
    <div ref={ref} className="bg-background border border-border rounded-md p-5 space-y-4">
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Question
        </div>
        <div className="mt-1 text-sm font-medium">{question}</div>
        <div className="mt-0.5 text-[11px] text-muted-foreground">
          {new Date(result.generated_at || Date.now()).toLocaleString()}
        </div>
      </div>

      {result.headline && (
        <p className="text-lg font-heading font-semibold leading-snug">{result.headline}</p>
      )}

      <div className="flex flex-wrap gap-1.5">
        {result.confidence && (
          <Badge variant={result.confidence === 'low' ? 'secondary' : 'outline'} className="text-[10px]">
            {CONFIDENCE_LABEL[result.confidence] ?? result.confidence}
          </Badge>
        )}
        {result.insufficient_data && (
          <Badge variant="secondary" className="text-[10px]">
            Insufficient data under k≥5 floor
          </Badge>
        )}
      </div>

      {result.knowledge_empty && (
        <div className="rounded-md border border-primary/40 bg-primary/5 px-3 py-2 text-xs">
          Knowledge base empty. Advice is data-only. Ingest methodology for expert-grade insight.
        </div>
      )}

      <Section label="Situation" body={situation} />
      <Section label="Why this matters" body={result.why_it_matters_markdown} />
      <Section label="What may be driving it" body={result.drivers_markdown} />

      {result.chart && safeSeries.length > 0 && (
        <div className="border-t border-border pt-4">
          <div className="text-sm font-medium mb-1">{result.chart.title}</div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              {result.chart.type === 'line' ? (
                <LineChart data={safeSeries}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="value" stroke="hsl(var(--primary))" strokeWidth={2} dot />
                </LineChart>
              ) : (
                <BarChart data={safeSeries}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="value" fill="hsl(var(--primary))" />
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <Section label="What to consider next" body={result.considerations_markdown} />
      <Section label="Action implications" body={result.action_implications_markdown} />

      {(result.insights?.length ?? 0) > 0 && (
        <div className="border-t border-border pt-4 space-y-4">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-primary">
            What the evidence suggests
          </div>
          {result.insights!.map((ins, i) => (
            <div key={i} className="rounded-md border border-border p-4 space-y-2">
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm font-semibold leading-snug">
                  {i + 1}. {ins.title}
                </p>
                {ins.confidence && (
                  <Badge
                    variant={ins.confidence === 'low' ? 'secondary' : 'outline'}
                    className="text-[10px] shrink-0"
                  >
                    {CONFIDENCE_LABEL[ins.confidence] ?? ins.confidence}
                  </Badge>
                )}
              </div>
              <Section label="Gap and likely root cause" body={ins.gap_root_cause_markdown} />
              <Section label="Why it matters" body={ins.why_it_matters_markdown} />
              <Section label="What to consider" body={ins.what_to_consider_markdown} />
              <Section label="Evidence basis" body={ins.evidence_basis_markdown} />
            </div>
          ))}
        </div>
      )}

      <Section
        label="What the evidence suggests"
        body={result.situation_markdown ? null : result.evidence_markdown}
      />
      <Section label="Evidence coverage" body={result.evidence_coverage_markdown} />

      {((result.citations?.length ?? 0) > 0 || (result.sources?.length ?? 0) > 0) && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {(result.sources ?? []).map((s, i) => (
            <Badge key={`s${i}`} variant="secondary" className="text-[10px]">
              {s}
            </Badge>
          ))}
          {(result.citations ?? []).map((c, i) => (
            <Badge key={`c${i}`} variant="outline" className="text-[10px]">
              {c}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
});
